/**
 * Turn one source-check operation, completed or interrupted, into durable audit records.
 *
 * This module is downstream of `reconcile`, never beside it. Citation decisions
 * arrive already made by the returned-URL gate; this adapter can preserve or
 * demote an admission, but has no path that promotes a rejection. Coverage then
 * counts every returned row, including rows the model never cited and rows the
 * gate rejected.
 *
 * Page text is ephemeral input. Its exact UTF-8 bytes are SHA-256 hashed for a
 * source snapshot and then discarded. Neither coverage metadata nor the audit
 * receipt contains the body.
 */

import {
  appendCoverageRound,
  createCoverageTrace,
  type AdmissionDecision,
  type CoverageBoundary,
  type CoverageResult,
  type CoverageRoundDraft,
  type ResultReach,
  type RoundCompletion,
} from '../core/coverage'
import { estimateSpendUsd } from '../core/models'
import {
  RESEARCH_AUDIT_RECEIPT_VERSION,
  type CitationAuditReceipt,
  type JsonValue,
  type RawSearchResultReceipt,
  type ResearchAuditReceipt,
  type ResearchAuditOutcome,
  type ResearchPromptReceipt,
  type SourceSnapshotReceipt,
  type StoredCoverageTrace,
} from '../core/research'
import { fnv1a64 } from '../core/similarity'
import { sha256Hex } from '../core/semanticGit'
import type { ModelStamp, Spend, Verification } from '../core/types'
import { canonicalUrl, isHttpUrl } from '../core/url'
import type { SearchOutcome, SeenSource } from './transport'
import type { CitationDecision } from './reason'

export const CHECK_AUDIT_COVERAGE_VERSION = 'check-audit-coverage-v1' as const

/** Stable protocol handles that evidence profiles can name before a pass runs. */
export const CHECK_AUDIT_QUERY_FAMILY_ID = 'claim-context-v1' as const
export const CHECK_AUDIT_RETRIEVAL_SOURCE_IDS = {
  web: 'provider-web',
  corpus: 'local-corpus',
  live: 'scholarly-index',
} as const

export interface CheckAuditWebBoundary {
  /** Provider/index version where one was exposed; `unavailable` otherwise. */
  searchVersion?: string
  /** Null explicitly means the provider exposed no corpus version. */
  corpusVersion?: string | null
  /** Defaults to the resulting verification date, then response completion. */
  searchedAt?: number
}

/**
 * One locally retrieved corpus or live-source row.
 *
 * `text` is accepted only so an HTTP-backed row can receive the same snapshot
 * hash as a fetched web page. It is never copied to output. `snippet` is a short
 * returned excerpt and is retained only in the raw-result receipt.
 */
export interface CheckAuditRetrievalRow {
  id: string
  /** Stable work/passage identity for coverage deduplication. */
  identity: string
  /** Stable locator when no public URL exists. Defaults to `identity`. */
  locator?: string
  title: string
  reach: ResultReach
  url?: string
  rank?: number | null
  documentId?: string
  passage?: number
  snippet?: string
  text?: string
  /** Exact retrieval-stage decision made before citation admission. */
  disposition?: string
}

/** Exact boundary around one supplemental retrieval batch. */
export interface CheckAuditRetrievalBatch {
  rows: readonly CheckAuditRetrievalRow[]
  queryText: string
  queryFamilyLabel: string
  retrievalSourceLabel: string
  retrievalSourceKind: string
  boundary: CoverageBoundary
  completion: RoundCompletion
  reportedResultCount: number | null
  /** Override for an individual index. The aggregate keeps the stable base id. */
  retrievalSourceId?: string
  retrievalSourceMeta?: JsonValue
  /** Individual attempted index batches are required unless explicitly false. */
  required?: boolean
}

export interface CheckAuditResponseReceipt {
  spend: Spend
  /** Explicit response stamp; it must agree with `spend.model`. */
  model: ModelStamp
  startedAt: number
  completedAt: number
  /** Defaults to completed for callers written before operation outcomes existed. */
  outcome?: ResearchAuditOutcome
}

/**
 * A retrieval surface whose request ended before it could return its ordinary
 * outcome object. Query/open events are the only web facts available on that
 * path, so they are carried explicitly rather than guessed back from an error.
 */
export interface CheckAuditInterruptedRound {
  surface: 'web' | 'corpus' | 'live'
  code: string
  reason: string
  searchedAt: number
  searchVersion: string
  corpusVersion: string | null
  /** Exact query text for local/index retrieval, when that request was known. */
  queryText?: string
  /** Exact provider query events observed before an interrupted web response. */
  queries?: readonly string[]
  /** Exact open-page events observed before an interrupted web response. */
  openedUrls?: readonly string[]
}

export interface CheckAuditInput {
  opId: string
  nodeId: string
  searchOutcome?: SearchOutcome | null
  citationDecisions: readonly CitationDecision[]
  verification: Verification | null
  /** Retrieval over user-owned corpus rows, when that surface participated. */
  corpus?: CheckAuditRetrievalBatch | null
  /** Retrieval over ephemeral public-index rows, when that surface participated. */
  live?: CheckAuditRetrievalBatch | null
  /** Per-index rows before scholarly merge, dedupe, and local ranking. */
  liveIndexes?: readonly CheckAuditRetrievalBatch[]
  webBoundary?: CheckAuditWebBoundary
  /** Exact prompt messages supplied to the model transport, in request order. */
  prompts: readonly ResearchPromptReceipt[]
  response: CheckAuditResponseReceipt
  /** At most one interrupted surface: the operation stops at its first failure. */
  interruptedRound?: CheckAuditInterruptedRound | null
}

export interface CheckAuditOutput {
  coverage: StoredCoverageTrace
  receipt: ResearchAuditReceipt
}

export class CheckAuditError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CheckAuditError'
  }
}

interface SourceSnapshotWork {
  key: string
  identity: string
  locator: string
  url: string | null
  text: string
  capturedAt: number
  id: string
}

interface AuditedDecision {
  input: CitationDecision
  receipt: CitationAuditReceipt
}

interface SurfaceWork {
  surface: 'web' | 'corpus' | 'live'
  /** Override when one retrieval surface landed more than one append-only round. */
  roundId?: string
  familyId: string
  queryId: string
  retrievalSourceId: string
  familyLabel: string
  retrievalSourceLabel: string
  retrievalSourceKind: string
  queryText: string
  familyMeta: JsonValue
  sourceMeta: JsonValue
  boundary: CoverageBoundary
  completion: RoundCompletion
  reportedResultCount: number | null
  results: CoverageResult<JsonValue>[]
  batch?: CheckAuditRetrievalBatch
  required?: boolean
}

const sameModel = (a: ModelStamp, b: ModelStamp): boolean =>
  a.provider === b.provider && a.id === b.id

const nonnegativeInteger = (value: number): number =>
  Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0

const nonblank = (value: string, label: string): string => {
  const held = value.trim()
  if (!held) throw new CheckAuditError(`${label} must be non-empty.`)
  return held
}

const validTime = (value: number, label: string): number => {
  if (!Number.isFinite(value) || value < 0) {
    throw new CheckAuditError(`${label} must be a non-negative finite timestamp.`)
  }
  return value
}

/** Keep ids beneath the research codec's 240-character boundary without collisions. */
function recordId(...parts: Array<string | number>): string {
  const raw = parts.join(':').replace(/\s+/g, '-').replace(/:+/g, ':')
  return raw.length <= 220 ? raw : `${raw.slice(0, 190)}:${fnv1a64(raw)}`
}

function webResultAddress(value: string): {
  identity: string
  locator: string
  url: string | null
} {
  if (isHttpUrl(value)) {
    const url = canonicalUrl(value)
    return { identity: url, locator: url, url }
  }
  const identity = `provider-web:invalid-url:${fnv1a64(value)}`
  return { identity, locator: identity, url: null }
}

const supplementalSourceId = (
  surface: 'corpus' | 'live',
  batch: CheckAuditRetrievalBatch,
): string => batch.retrievalSourceId?.trim() || CHECK_AUDIT_RETRIEVAL_SOURCE_IDS[surface]

const snapshotKey = (
  surface: 'corpus' | 'live',
  batch: CheckAuditRetrievalBatch,
  index: number,
): string => `${surface}:${supplementalSourceId(surface, batch)}:${index}`

const supplementalIdParts = (
  surface: 'corpus' | 'live',
  batch: CheckAuditRetrievalBatch,
): string[] => {
  const sourceId = supplementalSourceId(surface, batch)
  return sourceId === CHECK_AUDIT_RETRIEVAL_SOURCE_IDS[surface] ? [surface] : [surface, sourceId]
}

const supplementalRawId = (
  input: CheckAuditInput,
  surface: 'corpus' | 'live',
  batch: CheckAuditRetrievalBatch,
  index: number,
): string => recordId(input.opId, 'raw', ...supplementalIdParts(surface, batch), index + 1)

function resultReach(source: SeenSource): ResultReach {
  if (source.via === 'search') return 'snippet'
  if (source.text === undefined) return 'unreadable'
  return source.truncated ? 'snippet' : 'full-text'
}

function searchCompletion(search: SearchOutcome): RoundCompletion {
  const failures = search.failures.map((row) => `${row.tool}:${row.code}`)
  if (failures.length && search.sources.length === 0) {
    return {
      kind: 'failed',
      code: 'retrieval-failed',
      reason: `No result row returned; transport failures: ${failures.join(', ')}.`,
    }
  }
  if (search.exhausted) {
    return {
      kind: 'partial',
      code: 'transport-exhausted',
      reason: failures.length
        ? `The provider stopped before completing the search; failures: ${failures.join(', ')}.`
        : 'The provider stopped before completing the search.',
    }
  }
  if (failures.length) {
    return {
      kind: 'partial',
      code: 'retrieval-failure',
      reason: `Some retrieval actions failed: ${failures.join(', ')}.`,
    }
  }
  return { kind: 'complete' }
}

/**
 * Response failures already carried by an exact provider action are not a second
 * incomplete search. Remove one matching aggregate row per failed action; fetch
 * failures and exhaustion remain response-level and receive their own explicit
 * unattributed round below.
 */
function residualSearchCompletion(
  search: SearchOutcome,
  actions: readonly WebActionWork[],
): RoundCompletion {
  const failures = [...search.failures]
  for (const action of actions) {
    if (action.completion.kind !== 'failed') continue
    const code = action.completion.code
    const index = failures.findIndex(
      (failure) => failure.tool === 'search' && failure.code === code,
    )
    if (index >= 0) failures.splice(index, 1)
  }
  return searchCompletion({ ...search, failures })
}

function citationUrl(value: string, position: number): string {
  if (isHttpUrl(value)) return canonicalUrl(value)
  // `.invalid` is reserved and cannot resolve. The claimed value remains
  // recoverable in the query while the rejected receipt survives the codec's
  // HTTP-only citation field instead of vanishing on upsert.
  return `https://invalid.invalid/pathways-citation/${position}?claimed=${encodeURIComponent(value)}`
}

function promptRows(prompts: readonly ResearchPromptReceipt[]): ResearchPromptReceipt[] {
  const ids = new Set<string>()
  return prompts.map((prompt, index) => {
    const id = nonblank(prompt.id, `prompts[${index}].id`)
    if (ids.has(id)) throw new CheckAuditError(`Prompt id must be unique: ${id}`)
    ids.add(id)
    return { id, role: prompt.role, content: prompt.content }
  })
}

function checkSupplementalBatch(
  batch: CheckAuditRetrievalBatch,
  label: string,
): CheckAuditRetrievalBatch {
  nonblank(batch.queryText, `${label}.queryText`)
  nonblank(batch.queryFamilyLabel, `${label}.queryFamilyLabel`)
  nonblank(batch.retrievalSourceLabel, `${label}.retrievalSourceLabel`)
  nonblank(batch.retrievalSourceKind, `${label}.retrievalSourceKind`)
  if (batch.retrievalSourceId !== undefined) {
    nonblank(batch.retrievalSourceId, `${label}.retrievalSourceId`)
  }
  validTime(batch.boundary.searchedAt, `${label}.boundary.searchedAt`)
  nonblank(batch.boundary.searchVersion, `${label}.boundary.searchVersion`)
  if (batch.boundary.corpusVersion !== null) {
    nonblank(batch.boundary.corpusVersion, `${label}.boundary.corpusVersion`)
  }
  if (
    batch.reportedResultCount !== null &&
    (!Number.isInteger(batch.reportedResultCount) ||
      batch.reportedResultCount < 0 ||
      batch.reportedResultCount < batch.rows.length)
  ) {
    throw new CheckAuditError(
      `${label}.reportedResultCount must be null or cover every supplied row.`,
    )
  }
  const ids = new Set<string>()
  for (const [index, row] of batch.rows.entries()) {
    const id = nonblank(row.id, `${label}.rows[${index}].id`)
    nonblank(row.identity, `${label}.rows[${index}].identity`)
    if (row.locator !== undefined) nonblank(row.locator, `${label}.rows[${index}].locator`)
    nonblank(row.title, `${label}.rows[${index}].title`)
    if (ids.has(id)) throw new CheckAuditError(`${label} row id must be unique: ${id}`)
    ids.add(id)
    if (row.passage !== undefined && (!Number.isInteger(row.passage) || row.passage < 1)) {
      throw new CheckAuditError(`${label}.rows[${index}].passage must be a positive integer.`)
    }
  }
  return batch
}

function supplementAdmission(
  row: CheckAuditRetrievalRow,
  verification: Verification | null,
): AdmissionDecision {
  if (row.disposition && row.disposition !== 'offered' && row.disposition !== 'retained') {
    return {
      kind: 'rejected',
      rule: 'retrieval.pipeline-disposition.v1',
      code: row.disposition,
      reason: `The retrieval pipeline classified this row as ${row.disposition}.`,
    }
  }
  const admitted =
    row.documentId !== undefined &&
    row.passage !== undefined &&
    verification?.corpus?.cites.some(
      (cite) => cite.docId === row.documentId && cite.passage === row.passage,
    )
  return admitted
    ? {
        kind: 'admitted',
        rule: 'citation.corpus-passage-recorded.v1',
        reason: 'This exact document passage appears in the resulting verification.',
      }
    : {
        kind: 'rejected',
        rule: 'citation.corpus-passage-recorded.v1',
        code: 'not-recorded',
        reason: 'This retrieved row does not appear in the resulting verification.',
      }
}

function supplementMeta(
  input: CheckAuditInput,
  surface: 'corpus' | 'live',
  row: CheckAuditRetrievalRow,
  rawResultId: string,
  snapshotHash?: string,
): JsonValue {
  return {
    surface,
    opId: input.opId,
    nodeId: input.nodeId,
    rawResultId,
    title: row.title,
    ...(row.url ? { url: row.url.trim() } : {}),
    locator: row.locator?.trim() || row.identity,
    ...(row.disposition ? { disposition: row.disposition } : {}),
    ...(row.rank !== undefined ? { rank: row.rank } : {}),
    ...(row.documentId ? { documentId: row.documentId } : {}),
    ...(row.passage !== undefined ? { passage: row.passage } : {}),
    ...(snapshotHash ? { sourceSnapshotHash: snapshotHash } : {}),
  }
}

function usageOf(spend: Spend) {
  const estimate = estimateSpendUsd([spend])
  return {
    inputTokens: nonnegativeInteger(spend.inputTokens),
    outputTokens: nonnegativeInteger(spend.outputTokens),
    // Neither provider's shared Spend contract exposes a reasoning-token count.
    reasoningTokens: null,
    // An absent breakdown is unavailable, not zero.
    cacheReadInputTokens: spend.detail ? nonnegativeInteger(spend.detail.cacheRead) : null,
    cacheWriteInputTokens: spend.detail
      ? nonnegativeInteger(
          spend.detail.cacheWrite5m + spend.detail.cacheWrite30m + spend.detail.cacheWrite1h,
        )
      : null,
    searchRequests: nonnegativeInteger(spend.searches),
    estimatedUsd: estimate.unpriced.length ? null : estimate.usd,
  }
}

async function sourceSnapshots(input: CheckAuditInput): Promise<{
  receipts: SourceSnapshotReceipt[]
  hashes: Map<string, string>
}> {
  const capturedAt =
    input.webBoundary?.searchedAt ?? input.verification?.checkedAt ?? input.response.completedAt
  const work: SourceSnapshotWork[] = []
  for (const [index, source] of (input.searchOutcome?.sources ?? []).entries()) {
    if (source.text === undefined) continue
    const address = webResultAddress(source.url)
    work.push({
      key: `web:${index}`,
      ...address,
      text: source.text,
      capturedAt,
      id: recordId(input.opId, 'snapshot', 'web', index + 1),
    })
  }
  const supplemental = [
    ...(input.corpus ? [{ surface: 'corpus' as const, batch: input.corpus }] : []),
    ...(input.live ? [{ surface: 'live' as const, batch: input.live }] : []),
    ...(input.liveIndexes ?? []).map((batch) => ({ surface: 'live' as const, batch })),
  ]
  for (const { surface, batch } of supplemental) {
    for (const [index, row] of batch.rows.entries()) {
      if (row.text === undefined) continue
      const url = row.url && isHttpUrl(row.url) ? canonicalUrl(row.url) : null
      work.push({
        key: snapshotKey(surface, batch, index),
        identity: row.identity,
        locator: row.locator?.trim() || url || row.identity,
        url,
        text: row.text,
        capturedAt: batch.boundary.searchedAt,
        id: recordId(input.opId, 'snapshot', ...supplementalIdParts(surface, batch), index + 1),
      })
    }
  }
  const rows = await Promise.all(
    work.map(async (row) => {
      const sha256 = await sha256Hex(row.text)
      return {
        key: row.key,
        receipt: {
          id: row.id,
          identity: row.identity,
          locator: row.locator,
          url: row.url,
          capturedAt: row.capturedAt,
          sha256,
          bytes: new TextEncoder().encode(row.text).byteLength,
        } satisfies SourceSnapshotReceipt,
      }
    }),
  )
  return {
    receipts: rows.map((row) => row.receipt),
    hashes: new Map(rows.map((row) => [row.key, row.receipt.sha256])),
  }
}

function rawWebResults(
  input: CheckAuditInput,
  queryIds: readonly string[],
  retrievalSourceId: string,
  searchedAt: number,
  hashes: ReadonlyMap<string, string>,
): RawSearchResultReceipt[] {
  return (input.searchOutcome?.sources ?? []).map((source, index) => {
    const address = webResultAddress(source.url)
    return {
      id: recordId(input.opId, 'raw', 'web', index + 1),
      queryId: queryIds[index] ?? recordId(input.opId, 'query', 'web'),
      retrievalSourceId,
      rank: index + 1,
      ...address,
      title: source.title,
      returnedAt: searchedAt,
      ...(hashes.get(`web:${index}`) ? { sourceSnapshotHash: hashes.get(`web:${index}`)! } : {}),
      raw: {
        surface: 'web',
        via: source.via,
        truncated: source.truncated === true,
        pageTextReturned: source.text !== undefined,
        validHttpUrl: address.url !== null,
      },
    }
  })
}

interface WebActionWork {
  providerActionId: string
  queryId: string
  roundId: string
  exactQueries: readonly string[]
  sourceIndexes: readonly number[]
  completion: RoundCompletion
}

interface WebAttribution {
  actions: readonly WebActionWork[]
  fallbackQueryId: string
  fallbackRoundId: string
  unassignedSourceIndexes: readonly number[]
  sourceQueryIds: readonly string[]
}

/**
 * Relate an optional provider action ledger back to the one aggregate candidate set.
 *
 * A structured action is allowed to repeat an aggregate source returned by an
 * earlier action. It is never allowed to introduce a source outside the
 * aggregate: that set is still the citation gate and remains the only source of
 * raw-result receipts.
 */
function webAttribution(input: CheckAuditInput): WebAttribution {
  const search = input.searchOutcome!
  const aggregateQueryId = recordId(input.opId, 'query', 'web')
  const aggregateRoundId = recordId(input.opId, 'round', 'web')
  if (!search.rounds?.length) {
    return {
      actions: [],
      fallbackQueryId: aggregateQueryId,
      fallbackRoundId: aggregateRoundId,
      unassignedSourceIndexes: search.sources.map((_source, index) => index),
      sourceQueryIds: search.sources.map(() => aggregateQueryId),
    }
  }

  const actionIds = new Set<string>()
  const claimed = new Set<number>()
  const sourceQueryIds: Array<string | undefined> = search.sources.map(() => undefined)
  const actions = search.rounds.map((round, actionIndex): WebActionWork => {
    const providerActionId = nonblank(round.id, `searchOutcome.rounds[${actionIndex}].id`)
    if (actionIds.has(providerActionId)) {
      throw new CheckAuditError(`Provider search action id must be unique: ${providerActionId}`)
    }
    actionIds.add(providerActionId)
    const queryId = recordId(input.opId, 'query', 'web', 'action', actionIndex + 1)
    const sourceIndexes: number[] = []
    for (const [sourceIndex, source] of round.sources.entries()) {
      const byReference = search.sources.indexOf(source)
      const identity = webResultAddress(source.url).identity
      const aggregateIndex =
        byReference >= 0
          ? byReference
          : search.sources.findIndex(
              (candidate) => webResultAddress(candidate.url).identity === identity,
            )
      if (aggregateIndex < 0) {
        throw new CheckAuditError(
          `searchOutcome.rounds[${actionIndex}].sources[${sourceIndex}] is outside the aggregate citation gate.`,
        )
      }
      if (!sourceIndexes.includes(aggregateIndex)) sourceIndexes.push(aggregateIndex)
      claimed.add(aggregateIndex)
      sourceQueryIds[aggregateIndex] ??= queryId
    }
    const exactQueries = round.queries.map((query) => query)
    return {
      providerActionId,
      queryId,
      roundId: recordId(input.opId, 'round', 'web', 'action', actionIndex + 1),
      exactQueries,
      sourceIndexes,
      completion:
        round.completion.kind === 'failed'
          ? {
              kind: 'failed',
              code: round.completion.code,
              reason: round.completion.reason,
            }
          : { kind: 'complete' },
    }
  })
  const unassignedSourceIndexes = search.sources
    .map((_source, index) => index)
    .filter((index) => !claimed.has(index))
  const fallbackQueryId = recordId(input.opId, 'query', 'web', 'unattributed')
  return {
    actions,
    fallbackQueryId,
    fallbackRoundId: recordId(input.opId, 'round', 'web', 'unattributed'),
    unassignedSourceIndexes,
    sourceQueryIds: sourceQueryIds.map((queryId) => queryId ?? fallbackQueryId),
  }
}

function rawInterruptedWebResults(input: CheckAuditInput): RawSearchResultReceipt[] {
  const interrupted = input.interruptedRound
  if (!interrupted || interrupted.surface !== 'web') return []
  const queryId = recordId(input.opId, 'query', 'web')
  return (interrupted.openedUrls ?? []).map((opened, index) => {
    const address = webResultAddress(opened)
    return {
      id: recordId(input.opId, 'raw', 'web-interrupted', index + 1),
      queryId,
      retrievalSourceId: CHECK_AUDIT_RETRIEVAL_SOURCE_IDS.web,
      rank: index + 1,
      ...address,
      // The transport exposed no title before it failed. A valid URL remains the
      // existing honest fallback; an unusable value gets a stable non-web locator.
      title: address.url ?? address.locator,
      returnedAt: interrupted.searchedAt,
      raw: {
        surface: 'web',
        observedEvent: 'open',
        operationInterrupted: true,
        pageTextReturned: false,
        validHttpUrl: address.url !== null,
      },
    }
  })
}

function rawSupplementalResults(
  input: CheckAuditInput,
  surface: 'corpus' | 'live',
  batch: CheckAuditRetrievalBatch,
  queryId: string,
  retrievalSourceId: string,
  hashes: ReadonlyMap<string, string>,
): RawSearchResultReceipt[] {
  return batch.rows.map((row, index) => {
    const url = row.url && isHttpUrl(row.url) ? canonicalUrl(row.url) : null
    const snapshotHash = hashes.get(snapshotKey(surface, batch, index))
    return {
      id: supplementalRawId(input, surface, batch, index),
      queryId,
      retrievalSourceId,
      rank: row.rank === undefined ? index + 1 : row.rank,
      identity: row.identity,
      locator: row.locator?.trim() || url || row.identity,
      url,
      title: row.title,
      ...(row.snippet ? { snippet: row.snippet.slice(0, 1_000) } : {}),
      returnedAt: batch.boundary.searchedAt,
      ...(snapshotHash ? { sourceSnapshotHash: snapshotHash } : {}),
      raw: {
        surface,
        ...(row.documentId ? { documentId: row.documentId } : {}),
        ...(row.passage !== undefined ? { passage: row.passage } : {}),
        ...(row.disposition ? { disposition: row.disposition } : {}),
      },
    }
  })
}

function auditDecisions(
  input: CheckAuditInput,
  rawWeb: readonly RawSearchResultReceipt[],
): AuditedDecision[] {
  const rawByIdentity = new Map<string, RawSearchResultReceipt>()
  for (const row of rawWeb) {
    if (!row.url || !isHttpUrl(row.url)) continue
    const identity = canonicalUrl(row.url)
    if (!rawByIdentity.has(identity)) rawByIdentity.set(identity, row)
  }
  const verificationSources = new Set(
    (input.verification?.sources ?? []).map((source) => canonicalUrl(source.url)),
  )
  return input.citationDecisions.map((decision, position) => {
    const matchedIdentity = decision.matchedIdentity
      ? canonicalUrl(decision.matchedIdentity)
      : undefined
    const raw = matchedIdentity ? rawByIdentity.get(matchedIdentity) : undefined
    const URLMatches =
      raw !== undefined &&
      raw.url !== null &&
      isHttpUrl(decision.url) &&
      canonicalUrl(decision.url) === canonicalUrl(raw.url)
    const recorded =
      raw !== undefined && raw.url !== null && verificationSources.has(canonicalUrl(raw.url))
    const admitted = decision.decision === 'admitted' && URLMatches && recorded
    let finalDecision: CitationAuditReceipt['decision'] = decision.decision
    let rule = decision.rule
    let reason = decision.reason
    if (decision.decision === 'admitted' && !admitted) {
      finalDecision = 'rejected'
      rule =
        raw && URLMatches
          ? 'research.citation-recorded-in-verification.v1'
          : 'research.citation-returned.v1'
      reason =
        raw && URLMatches
          ? 'Rejected by the audit adapter because the resulting verification did not retain this returned URL.'
          : 'Rejected by the audit adapter because no exact returned search row matched this citation.'
    }
    return {
      input: decision,
      receipt: {
        id: recordId(input.opId, 'citation', position + 1, decision.index),
        rawResultId: raw?.id ?? null,
        url: citationUrl(decision.url, position + 1),
        title: decision.title.trim() || '(title not supplied)',
        decision: finalDecision,
        rule,
        reason,
        ...(raw ? { sourceSnapshotHash: raw.sourceSnapshotHash } : {}),
      },
    }
  })
}

function webAdmission(
  raw: RawSearchResultReceipt,
  decisions: readonly AuditedDecision[],
): AdmissionDecision {
  const linked = decisions.filter((row) => row.receipt.rawResultId === raw.id)
  const admitted = linked.find((row) => row.receipt.decision === 'admitted')
  if (admitted) {
    return {
      kind: 'admitted',
      rule: admitted.receipt.rule,
      ...(admitted.receipt.reason ? { reason: admitted.receipt.reason } : {}),
    }
  }
  const rejected = linked[0]
  return rejected
    ? {
        kind: 'rejected',
        rule: rejected.receipt.rule,
        code: 'citation-rejected',
        reason: rejected.receipt.reason,
      }
    : {
        kind: 'rejected',
        rule: 'citation.proposed-by-model.v1',
        code: 'not-proposed',
        reason: 'The search returned this row, but the model did not propose it as a citation.',
      }
}

function webSurfaces(
  input: CheckAuditInput,
  raw: readonly RawSearchResultReceipt[],
  decisions: readonly AuditedDecision[],
  hashes: ReadonlyMap<string, string>,
  attribution: WebAttribution,
): SurfaceWork[] {
  const search = input.searchOutcome
  if (!search) return []
  const familyId = CHECK_AUDIT_QUERY_FAMILY_ID
  const retrievalSourceId = CHECK_AUDIT_RETRIEVAL_SOURCE_IDS.web
  const searchedAt =
    input.webBoundary?.searchedAt ?? input.verification?.checkedAt ?? input.response.completedAt
  const boundary = {
    searchedAt,
    searchVersion: input.webBoundary?.searchVersion?.trim() || 'unavailable',
    corpusVersion: input.webBoundary?.corpusVersion ?? null,
  }
  const surface = (
    queryId: string,
    roundId: string,
    exactQueries: readonly string[],
    sourceIndexes: readonly number[],
    completion: RoundCompletion,
    queryAttribution: 'provider-search-action' | 'aggregate-only' | 'unattributed-aggregate',
    providerActionId?: string,
  ): SurfaceWork => ({
    surface: 'web',
    roundId,
    familyId,
    queryId,
    retrievalSourceId,
    familyLabel: 'Provider-issued query batch',
    retrievalSourceLabel: `${input.response.model.name} web search`,
    retrievalSourceKind: 'web',
    queryText: exactQueries.length
      ? JSON.stringify(exactQueries)
      : '[exact provider query unavailable]',
    familyMeta: { surface: 'web', exactQueries: [...exactQueries], queryAttribution },
    sourceMeta: {
      surface: 'web',
      provider: input.response.model.provider,
      modelId: input.response.model.id,
      pageTextCapabilityObserved: search.pageText,
      actionGroupingObserved: attribution.actions.length > 0,
    },
    boundary,
    completion,
    reportedResultCount: sourceIndexes.length,
    results: sourceIndexes.map((index, position) => {
      const source = search.sources[index]!
      const rawRow = raw[index]!
      const snapshotHash = hashes.get(`web:${index}`)
      return {
        id: recordId(input.opId, 'result', 'web', roundId, position + 1),
        identity: rawRow.identity,
        reach: resultReach(source),
        admission: webAdmission(rawRow, decisions),
        meta: {
          surface: 'web',
          opId: input.opId,
          nodeId: input.nodeId,
          ...(rawRow.queryId === queryId ? { rawResultId: rawRow.id } : {}),
          ...(providerActionId ? { providerSearchActionId: providerActionId } : {}),
          title: source.title,
          url: source.url,
          via: source.via,
          truncated: source.truncated === true,
          ...(snapshotHash ? { sourceSnapshotHash: snapshotHash } : {}),
        },
      }
    }),
  })

  if (!attribution.actions.length) {
    const exactQueries = search.queries.map((query) => query)
    return [
      surface(
        attribution.fallbackQueryId,
        attribution.fallbackRoundId,
        exactQueries,
        attribution.unassignedSourceIndexes,
        searchCompletion(search),
        'aggregate-only',
      ),
    ]
  }

  const residual = residualSearchCompletion(search, attribution.actions)
  const actions = attribution.actions.map((action) =>
    surface(
      action.queryId,
      action.roundId,
      action.exactQueries,
      action.sourceIndexes,
      action.completion,
      'provider-search-action',
      action.providerActionId,
    ),
  )
  if (attribution.unassignedSourceIndexes.length) {
    actions.push(
      surface(
        attribution.fallbackQueryId,
        attribution.fallbackRoundId,
        search.queries,
        attribution.unassignedSourceIndexes,
        residual,
        'unattributed-aggregate',
      ),
    )
  } else if (residual.kind !== 'complete') {
    // No provider action owns a fetch failure or response exhaustion. A separate
    // zero-result row keeps that weakness exact instead of pinning it on the last
    // query merely because that query happened to land last.
    actions.push(
      surface(
        attribution.fallbackQueryId,
        attribution.fallbackRoundId,
        [],
        [],
        residual,
        'unattributed-aggregate',
      ),
    )
  }
  return actions
}

function supplementalSurface(
  input: CheckAuditInput,
  surface: 'corpus' | 'live',
  rawBatch: CheckAuditRetrievalBatch,
  hashes: ReadonlyMap<string, string>,
): SurfaceWork | null {
  const retrievalSourceId = supplementalSourceId(surface, rawBatch)
  const batch = checkSupplementalBatch(rawBatch, `${surface}:${retrievalSourceId}`)
  return {
    surface,
    familyId: CHECK_AUDIT_QUERY_FAMILY_ID,
    queryId: recordId(input.opId, 'query', ...supplementalIdParts(surface, batch)),
    retrievalSourceId,
    familyLabel: batch.queryFamilyLabel,
    retrievalSourceLabel: batch.retrievalSourceLabel,
    retrievalSourceKind: batch.retrievalSourceKind,
    queryText: batch.queryText,
    familyMeta: { surface },
    sourceMeta: {
      surface,
      ...(batch.retrievalSourceMeta === undefined ? {} : { batch: batch.retrievalSourceMeta }),
    },
    boundary: { ...batch.boundary },
    completion: { ...batch.completion },
    reportedResultCount: batch.reportedResultCount,
    results: batch.rows.map((row, index) => ({
      id: recordId(input.opId, 'result', ...supplementalIdParts(surface, batch), index + 1, row.id),
      identity: row.identity,
      reach: row.reach,
      admission: supplementAdmission(row, input.verification),
      meta: supplementMeta(
        input,
        surface,
        row,
        supplementalRawId(input, surface, batch, index),
        hashes.get(snapshotKey(surface, batch, index)),
      ),
    })),
    batch,
    required: batch.required,
  }
}

function interruptedSurface(
  input: CheckAuditInput,
  rawWeb: readonly RawSearchResultReceipt[],
): SurfaceWork | null {
  const interrupted = input.interruptedRound
  if (!interrupted) return null
  const surface = interrupted.surface
  const isWeb = surface === 'web'
  const exactQueries = (interrupted.queries ?? []).map((query) => query.trim()).filter(Boolean)
  const queryText = isWeb
    ? exactQueries.length
      ? JSON.stringify(exactQueries)
      : '[provider returned no query event before the operation ended]'
    : interrupted.queryText?.trim() || '[retrieval query unavailable before the operation ended]'
  return {
    surface,
    familyId: CHECK_AUDIT_QUERY_FAMILY_ID,
    queryId: recordId(input.opId, 'query', surface),
    retrievalSourceId: CHECK_AUDIT_RETRIEVAL_SOURCE_IDS[surface],
    familyLabel: 'Claim wording and reasoning',
    retrievalSourceLabel: {
      web: `${input.response.model.name} web search`,
      corpus: 'User document corpus',
      live: 'Public scholarly indexes',
    }[surface],
    retrievalSourceKind: {
      web: 'web',
      corpus: 'local-corpus',
      live: 'scholarly-index',
    }[surface],
    queryText,
    familyMeta: {
      surface,
      operationInterrupted: true,
      ...(isWeb ? { exactQueries } : {}),
    },
    sourceMeta: {
      surface,
      operationInterrupted: true,
      code: interrupted.code,
      ...(isWeb
        ? {
            observedOpenedUrls: [...(interrupted.openedUrls ?? [])],
            invalidOpenedUrlCount: (interrupted.openedUrls ?? []).filter((url) => !isHttpUrl(url))
              .length,
          }
        : {}),
    },
    boundary: {
      searchedAt: interrupted.searchedAt,
      searchVersion: interrupted.searchVersion,
      corpusVersion: interrupted.corpusVersion,
    },
    completion: {
      kind: 'failed',
      code: interrupted.code,
      reason: interrupted.reason,
    },
    reportedResultCount: null,
    results: isWeb
      ? rawWeb.map((row, index) => ({
          id: recordId(input.opId, 'result', 'web-interrupted', index + 1),
          identity: row.identity,
          reach: 'unreadable' as const,
          admission: {
            kind: 'rejected' as const,
            rule: 'research.source-check-completed.v1',
            code: 'operation-interrupted',
            reason: 'The operation ended before this opened URL could be admitted as evidence.',
          },
          meta: {
            surface: 'web',
            opId: input.opId,
            nodeId: input.nodeId,
            rawResultId: row.id,
            url: row.url,
            operationInterrupted: true,
          },
        }))
      : [],
  }
}

function coverageTrace(
  input: CheckAuditInput,
  surfaces: readonly SurfaceWork[],
): StoredCoverageTrace {
  const baseSurfaces = (['web', 'corpus', 'live'] as const).map((surface) => {
    const sourceId = CHECK_AUDIT_RETRIEVAL_SOURCE_IDS[surface]
    return {
      surface,
      sourceId,
      landed: surfaces.find((row) => row.retrievalSourceId === sourceId),
    }
  })
  const baseIds = new Set<string>(baseSurfaces.map((row) => row.sourceId))
  const additionalSources = surfaces.filter((surface) => !baseIds.has(surface.retrievalSourceId))
  let trace = createCoverageTrace<JsonValue, JsonValue, JsonValue>({
    id: recordId(input.opId, 'coverage'),
    version: CHECK_AUDIT_COVERAGE_VERSION,
    // The family and all three surfaces are declared independently of what
    // happened to be available. Missing rounds therefore become explicit blind
    // spots instead of disappearing from the denominator.
    queryFamilies: [
      {
        id: CHECK_AUDIT_QUERY_FAMILY_ID,
        label: 'Claim wording, context, and reasoning',
        meta: { predeclared: true, exactQueriesLiveOnRounds: true },
      },
    ],
    retrievalSources: [
      ...baseSurfaces.map(({ surface, sourceId, landed }) => {
        const fallback = {
          web: { label: 'Provider web search', kind: 'web' },
          corpus: { label: 'User document corpus', kind: 'local-corpus' },
          live: { label: 'Public scholarly indexes', kind: 'scholarly-index' },
        }[surface]
        return {
          id: sourceId,
          label: landed?.retrievalSourceLabel ?? fallback.label,
          kind: landed?.retrievalSourceKind ?? fallback.kind,
          meta: landed?.sourceMeta ?? { surface, predeclared: true },
        }
      }),
      ...additionalSources.map((source) => ({
        id: source.retrievalSourceId,
        label: source.retrievalSourceLabel,
        kind: source.retrievalSourceKind,
        meta: source.sourceMeta,
      })),
    ],
    requiredCombinations: [
      ...baseSurfaces.map(({ sourceId }) => ({
        queryFamilyId: CHECK_AUDIT_QUERY_FAMILY_ID,
        retrievalSourceId: sourceId,
      })),
      ...additionalSources
        .filter((source) => source.required !== false)
        .map((source) => ({
          queryFamilyId: CHECK_AUDIT_QUERY_FAMILY_ID,
          retrievalSourceId: source.retrievalSourceId,
        })),
    ],
  })
  for (const surface of surfaces) {
    const draft: CoverageRoundDraft<JsonValue> = {
      id:
        surface.roundId ??
        (surface.retrievalSourceId === CHECK_AUDIT_RETRIEVAL_SOURCE_IDS[surface.surface]
          ? recordId(input.opId, 'round', surface.surface)
          : recordId(input.opId, 'round', surface.surface, surface.retrievalSourceId)),
      query: {
        id: surface.queryId,
        text: surface.queryText,
        familyId: surface.familyId,
      },
      retrievalSourceId: surface.retrievalSourceId,
      boundary: { ...surface.boundary },
      completion: { ...surface.completion },
      reportedResultCount: surface.reportedResultCount,
      results: surface.results,
    }
    trace = appendCoverageRound(trace, draft)
  }
  return trace
}

function validateInput(input: CheckAuditInput): void {
  nonblank(input.opId, 'opId')
  nonblank(input.nodeId, 'nodeId')
  const startedAt = validTime(input.response.startedAt, 'response.startedAt')
  const completedAt = validTime(input.response.completedAt, 'response.completedAt')
  if (completedAt < startedAt) {
    throw new CheckAuditError('response.completedAt must not precede response.startedAt.')
  }
  if (!sameModel(input.response.model, input.response.spend.model)) {
    throw new CheckAuditError('The response model and spend model must identify the same model.')
  }
  if (input.webBoundary?.searchedAt !== undefined) {
    validTime(input.webBoundary.searchedAt, 'webBoundary.searchedAt')
  }
  if (input.corpus) checkSupplementalBatch(input.corpus, 'corpus')
  if (input.live) checkSupplementalBatch(input.live, 'live')
  for (const [index, batch] of (input.liveIndexes ?? []).entries()) {
    checkSupplementalBatch(batch, `liveIndexes[${index}]`)
  }
  const outcome = input.response.outcome
  if (outcome && outcome.kind !== 'completed') {
    nonblank(outcome.code, 'response.outcome.code')
    nonblank(outcome.reason, 'response.outcome.reason')
  }
  const interrupted = input.interruptedRound
  if (interrupted) {
    nonblank(interrupted.code, 'interruptedRound.code')
    nonblank(interrupted.reason, 'interruptedRound.reason')
    validTime(interrupted.searchedAt, 'interruptedRound.searchedAt')
    nonblank(interrupted.searchVersion, 'interruptedRound.searchVersion')
    if (interrupted.corpusVersion !== null) {
      nonblank(interrupted.corpusVersion, 'interruptedRound.corpusVersion')
    }
    if (interrupted.queryText !== undefined) {
      nonblank(interrupted.queryText, 'interruptedRound.queryText')
    }
    for (const [index, query] of (interrupted.queries ?? []).entries()) {
      nonblank(query, `interruptedRound.queries[${index}]`)
    }
    const hasOrdinarySurface =
      (interrupted.surface === 'web' &&
        input.searchOutcome !== undefined &&
        input.searchOutcome !== null) ||
      (interrupted.surface === 'corpus' && input.corpus !== undefined && input.corpus !== null) ||
      (interrupted.surface === 'live' &&
        ((input.live !== undefined && input.live !== null) || (input.liveIndexes?.length ?? 0) > 0))
    if (hasOrdinarySurface) {
      throw new CheckAuditError(
        `interruptedRound cannot replace an ordinary ${interrupted.surface} outcome.`,
      )
    }
  }
}

/**
 * Build both durable records from one already-reconciled source check.
 *
 * Pure except for Web Crypto's SHA-256 primitive. The same input produces the
 * same trace and receipt, and no caller-owned object is mutated.
 */
export async function buildCheckAudit(input: CheckAuditInput): Promise<CheckAuditOutput> {
  validateInput(input)
  const snapshots = await sourceSnapshots(input)

  // The web round owns these stable ids. Build its raw rows before citation
  // auditing so a claimed admission must point back to one exact returned row.
  const webSourceId = CHECK_AUDIT_RETRIEVAL_SOURCE_IDS.web
  const webSearchedAt =
    input.webBoundary?.searchedAt ?? input.verification?.checkedAt ?? input.response.completedAt
  const attribution = input.searchOutcome ? webAttribution(input) : null
  const rawWeb = input.searchOutcome
    ? rawWebResults(
        input,
        attribution!.sourceQueryIds,
        webSourceId,
        webSearchedAt,
        snapshots.hashes,
      )
    : rawInterruptedWebResults(input)
  const decisions = auditDecisions(input, rawWeb)
  const ordinarySurfaces = [
    ...(attribution ? webSurfaces(input, rawWeb, decisions, snapshots.hashes, attribution) : []),
    ...(input.corpus ? [supplementalSurface(input, 'corpus', input.corpus, snapshots.hashes)] : []),
    ...(input.live ? [supplementalSurface(input, 'live', input.live, snapshots.hashes)] : []),
    ...(input.liveIndexes ?? []).map((batch) =>
      supplementalSurface(input, 'live', batch, snapshots.hashes),
    ),
  ].filter((surface): surface is SurfaceWork => surface !== null)
  const interrupted = interruptedSurface(input, rawWeb)
  const surfaces =
    interrupted && !ordinarySurfaces.some((surface) => surface.surface === interrupted.surface)
      ? [...ordinarySurfaces, interrupted]
      : ordinarySurfaces

  const rawSupplemental = surfaces.flatMap((surface) =>
    surface.surface === 'web' || !surface.batch
      ? []
      : rawSupplementalResults(
          input,
          surface.surface,
          surface.batch,
          surface.queryId,
          surface.retrievalSourceId,
          snapshots.hashes,
        ),
  )
  const receipt: ResearchAuditReceipt = {
    version: RESEARCH_AUDIT_RECEIPT_VERSION,
    id: recordId(input.opId, 'source-check-audit'),
    opId: input.opId,
    kind: 'source-check',
    model: { ...input.response.model },
    prompts: promptRows(input.prompts),
    startedAt: input.response.startedAt,
    completedAt: input.response.completedAt,
    usage: usageOf(input.response.spend),
    outcome: input.response.outcome ?? { kind: 'completed' },
    rawSearchResults: [...rawWeb, ...rawSupplemental],
    citations: decisions.map((decision) => decision.receipt),
    sourceSnapshots: snapshots.receipts,
  }
  return { coverage: coverageTrace(input, surfaces), receipt }
}
